import { Eraser, Pause, Play, Unlink } from 'lucide-react';
import type { LiveSummary, Platform } from '@multimod/shared';
import { useMsgRate } from '@/features/panel/hooks/useMsgRate';
import { PlatformIcon } from '@/components/ui/platform-icon';
import { avatarColors, avatarInitials } from '@/features/panel/lib/avatar';
import { formatUptime } from '@/features/panel/lib/time';
import { FiltersPopover } from './FiltersPopover.js';
import type { ChatFeedStore } from '@/features/chat-feed/store';

// Identidade canônica do canal conectado (vem do `me.connections.<platform>`).
// `externalId` alimenta a paleta determinística do avatar quando não há live.
export interface ChannelConnection {
  displayName: string;
  externalId: string;
}

interface PlatformInfo {
  live: LiveSummary | null;
  connection: ChannelConnection | null;
  // Quando definido, renderiza botão Unlink na linha — usado pra desanexar
  // lives **secondary** (a primary é trocada via StreamerPill dropdown).
  // Indefinido nas plataformas em que a live é primary ou não está anexada.
  onDetach?: () => void;
  // #147: live encerrada (sumiu de /me/streams por N ciclos, ou 404 not_live no
  // re-resolve YouTube). Mantém o último valor conhecido mas com badge "encerrada"
  // e visual dessaturado — não finge que segue ao vivo.
  stale?: boolean;
}

// Avatar 32×32 com iniciais + dot 8×8 de status (pulse animado quando ao vivo).
// Dessaturado quando o canal está conectado mas sem live ativa.
function ChannelAvatar({
  displayName,
  externalId,
  isLive,
}: {
  displayName: string;
  externalId: string;
  isLive: boolean;
}) {
  const { bg, fg } = avatarColors(externalId);
  const initials = avatarInitials(displayName);
  return (
    <div className="relative shrink-0">
      <div
        aria-hidden="true"
        className={
          'inline-flex items-center justify-center w-8 h-8 rounded-full text-xs font-semibold leading-none select-none transition-[filter,opacity] duration-fast ' +
          (isLive ? '' : 'opacity-60 grayscale')
        }
        // backgroundColor/color têm que ficar inline porque a paleta é dinâmica
        // (avatarColors(externalId)). Dimensão usa classes (w-8 h-8, P14 espírito).
        style={{ backgroundColor: bg, color: fg }}
      >
        {initials}
      </div>
      {isLive && (
        <span
          aria-hidden="true"
          // ring-2 ring-bg-elevated cria o "punch-out" contra o fundo da section.
          // animate-pulse vira no-op via globals.css @media (prefers-reduced-motion: reduce). (P16)
          className="absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full bg-success ring-2 ring-bg-elevated animate-pulse"
        />
      )}
    </div>
  );
}

// Rate é calculado uma vez em `ChannelSummary` (pai) e passado via prop —
// evita chamadas duplicadas do hook quando o pai também precisa pra somar
// o total combinado.
function PlatformRow({
  info,
  platform,
  rate,
}: {
  info: PlatformInfo;
  platform: Platform;
  rate: number;
}) {
  const { live, connection, onDetach, stale } = info;
  const ended = Boolean(live) && Boolean(stale);
  // Stale conta como "não ao vivo" pro visual (avatar dessaturado, sem métricas
  // ao vivo) — o badge "encerrada" assume o lugar das métricas.
  const isLive = Boolean(live) && !stale;

  if (!live && !connection) return null;

  const displayName = live?.displayName ?? connection?.displayName ?? '';
  const avatarKey = live?.login ?? connection?.externalId ?? '';
  const platIconColor = isLive
    ? platform === 'twitch'
      ? 'text-platform-twitch'
      : 'text-platform-youtube'
    : 'text-fg-subtle';

  return (
    <div
      className="flex items-center gap-2 min-w-0 max-w-[340px] flex-1"
      data-platform={platform}
    >
      <ChannelAvatar displayName={displayName} externalId={avatarKey} isLive={isLive} />
      <div className="flex flex-col leading-[1.15] min-w-0 flex-1">
        {/* leading-[1.15]: exceção pontual — display denso (P14 nota) */}
        {/* Linha 1: ícone + nome (à esquerda), métricas (à direita) */}
        <div className="flex items-center gap-2 min-w-0">
          <div className="flex items-center gap-1.5 min-w-0">
            <PlatformIcon
              platform={platform}
              size={12}
              className={`${platIconColor} shrink-0`}
            />
            {/* tracking-[-0.005em]: exceção pontual entre tight e padrão */}
            <span className="text-sm font-semibold tracking-[-0.005em] truncate">
              {displayName}
            </span>
          </div>
          {isLive && live && (
            <span className="text-2xs text-fg-muted tabular-nums whitespace-nowrap ml-auto shrink-0">
              <span className="text-fg font-medium">{live.viewerCount.toLocaleString('pt-BR')}</span>
              <span> · </span>
              <span className="text-fg font-medium">{formatUptime(live.startedAt)}</span>
              <span> · </span>
              <span className="text-fg font-medium">{rate}</span>
              <span>/min</span>
            </span>
          )}
          {/* #147: badge no lugar das métricas quando a live encerrou. Tom neutro
              (info, não destructive — P13): é estado, não erro. */}
          {ended && (
            <span className="text-2xs uppercase tracking-[0.06em] font-medium text-fg-muted bg-bg-muted rounded px-1.5 py-0.5 whitespace-nowrap ml-auto shrink-0">
              encerrada
            </span>
          )}
        </div>
        {/* Linha 2: título (block-level, truncate funciona) ou placeholder */}
        {live ? (
          <div className={'truncate text-2xs mt-px ' + (ended ? 'text-fg-subtle' : 'text-fg-muted')}>
            {live.title || '—'}
          </div>
        ) : (
          <span className="text-2xs text-fg-subtle italic mt-px">
            Nenhuma live ativa — selecionar
          </span>
        )}
      </div>
      {/* Botão Unlink (#131) — só renderiza quando a plataforma tem live anexada
          (não-primary). Visual 28×28, hit area 44×44 via pseudo-element. */}
      {onDetach && isLive && (
        <button
          type="button"
          onClick={onDetach}
          aria-label={`Desanexar live ${displayName}`}
          title="Desanexar"
          data-testid={`detach-${platform}`}
          className="relative w-7 h-7 grid place-items-center rounded-md text-fg-muted hover:bg-bg-muted hover:text-destructive transition-colors duration-fast shrink-0 before:absolute before:-inset-2 before:content-['']"
        >
          <Unlink size={14} strokeWidth={1.75} aria-hidden="true" />
        </button>
      )}
    </div>
  );
}

function ActionBtn({
  label,
  onClick,
  pressed,
  children,
}: {
  label: string;
  onClick: () => void;
  pressed?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-ch-action
      aria-label={label}
      aria-pressed={pressed}
      title={label}
      className={
        'w-8 h-8 grid place-items-center rounded-md transition-colors duration-fast ' +
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ' +
        (pressed
          ? 'text-primary bg-primary/15 hover:bg-primary/20'
          : 'text-fg-muted hover:bg-bg-muted hover:text-fg')
      }
    >
      {children}
    </button>
  );
}

function ActionsCluster({ store }: { store: ChatFeedStore }) {
  const paused = store((s) => s.paused);
  const pause = store((s) => s.pause);
  const resume = store((s) => s.resume);
  const clearLocal = store((s) => s.clearLocal);

  return (
    <div className="flex items-center gap-1 shrink-0">
      <ActionBtn
        label={paused ? 'Retomar feed' : 'Pausar feed'}
        pressed={paused}
        onClick={paused ? resume : pause}
      >
        {paused ? <Play size={16} strokeWidth={1.75} /> : <Pause size={16} strokeWidth={1.75} />}
      </ActionBtn>
      <ActionBtn label="Limpar buffer" onClick={clearLocal}>
        {/* #114: Eraser (borracha) é semântico pra "limpar buffer"; RotateCcw sugeria "desfazer". */}
        <Eraser size={16} strokeWidth={1.75} />
      </ActionBtn>
      <FiltersPopover store={store} />
    </div>
  );
}

export function ChannelSummary({
  twitch,
  youtube,
  store,
}: {
  twitch: PlatformInfo;
  youtube: PlatformInfo;
  // Quando null, actions cluster fica escondido (sem live = sem o que pausar
  // ou filtrar). Resolve #115 — actions advertised sem live confundem.
  store: ChatFeedStore | null;
}) {
  // Fonte única do rate por plataforma. Total combinado e cada PlatformRow
  // lêem do mesmo cálculo, evitando duplicação de hook e leituras divergentes.
  const rateTwitch = useMsgRate(twitch.live?.liveChatId ?? null, twitch.live ? 'twitch' : null);
  const rateYoutube = useMsgRate(youtube.live?.liveChatId ?? null, youtube.live ? 'youtube' : null);
  const total = rateTwitch + rateYoutube;
  // #147: live stale não conta no total combinado (viewerCount congelado não deve
  // inflar a soma) nem mantém o bloco "viewers COMBINADO" se for a única.
  const twitchActive = !!twitch.live && !twitch.stale;
  const youtubeActive = !!youtube.live && !youtube.stale;
  const totalViewers =
    (twitchActive ? twitch.live!.viewerCount : 0) + (youtubeActive ? youtube.live!.viewerCount : 0);
  const hasLive = twitchActive || youtubeActive;

  const hasAny = twitch.live || twitch.connection || youtube.live || youtube.connection;
  if (!hasAny) {
    return (
      <section
        aria-label="Resumo das lives ativas"
        className="sticky top-14 z-[2] h-[76px] bg-bg-elevated border-b border-border flex items-center justify-center px-4"
      >
        <span className="text-sm text-fg-muted">Conecte uma live pra ver as métricas</span>
      </section>
    );
  }

  return (
    <section
      aria-label="Resumo das lives ativas"
      className="sticky top-14 z-[2] h-[76px] bg-bg-elevated border-b border-border"
    >
      {/* #86: conteúdo centrado em max-w-[1080px] pra acompanhar a coluna do
          feed (ChatFeed). A barra (bg/border) fica full-width; só o conteúdo
          alinha verticalmente com feed e composer em telas largas (≥1440px). */}
      <div className="mx-auto max-w-[1080px] w-full h-full flex items-center gap-4 px-4">
      <div className="flex items-center gap-5 min-w-0 flex-1">
        <PlatformRow info={twitch} platform="twitch" rate={rateTwitch} />
        <PlatformRow info={youtube} platform="youtube" rate={rateYoutube} />
      </div>

      <div className="w-px h-10 bg-border shrink-0" aria-hidden="true" />

      <div className="flex flex-col leading-[1.15] px-3.5 border border-border bg-bg-card rounded-md h-12 justify-center tabular-nums shrink-0">
        {/* leading-[1.15]: exceção pontual — display denso */}
        <span className="text-sm font-semibold tracking-[-0.005em] flex items-baseline gap-1">
          <span>{total}</span>
          <span className="text-2xs text-fg-muted font-normal" aria-hidden="true">msg/min</span>
        </span>
        <span className="text-2xs text-fg-subtle uppercase tracking-[0.08em] font-mono mt-px">COMBINADO</span>
      </div>

      {hasLive && (
        <div className="flex flex-col leading-[1.15] px-3.5 border border-border bg-bg-card rounded-md h-12 justify-center tabular-nums shrink-0">
          <span className="text-sm font-semibold tracking-[-0.005em] flex items-baseline gap-1">
            <span>{totalViewers.toLocaleString('pt-BR')}</span>
            <span className="text-2xs text-fg-muted font-normal" aria-hidden="true">viewers</span>
          </span>
          <span className="text-2xs text-fg-subtle uppercase tracking-[0.08em] font-mono mt-px">COMBINADO</span>
        </div>
      )}

      {store && (
        <>
          <div className="w-px h-10 bg-border shrink-0" aria-hidden="true" />
          <ActionsCluster store={store} />
        </>
      )}
      </div>
    </section>
  );
}
